import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { useForm, Controller } from 'react-hook-form';
import CurrencyInput from 'react-currency-input-field';
import { yupResolver } from '@hookform/resolvers/yup';
import { TypeOf } from 'yup';
import _debounce from 'lodash/debounce';
import getSuggestedItems, { ItemSuggestion } from '../../api/internal/getSuggestedItems';
import Autocomplete from '../../components/Autocomplete';
import Button from '../../components/Button';
import Link from '../../components/Link';
import {
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  Checkbox,
  Stack,
  usePopoverOpen,
  Text,
} from '@spaceshipapp/cambridge';
import itemSchema from '../../schemas/ship/item';
import { currencyList } from '../../utils/currency';
import * as analytics from '../../analytics/quotation';
import Logger from '../../utils/logger';
import email from '../../public/item/email.svg';
import {
  exportRequirementLinkStyle,
  helpMessageStyle,
  inlineStyle,
  mailTextStyle,
  selectStyle,
  stackStyle,
  supportTextStyle,
} from './styled';
import { makeCountryAutocompleteProps } from '../../hooks/useCountryAutocomplete';
import { sortBy } from 'lodash';
import { COUNTRIES } from '../../utils/countries';
import Trans from 'next-translate/Trans';

interface ItemFormProps {
  hkhsCode?: string;
  description?: string;
  currency?: string;
  naturalAmount?: number;
  qty?: number;
  isIncludesFood?: boolean;
  isIncludesBattery?: boolean;
  countryOfManufacture?: string;
  remarks?: string;
  onSubmit(data: TypeOf<typeof itemSchema>): void;
  onRemove?(): void;
}

const ItemForm = (props: ItemFormProps): React.ReactElement => {
  const { t, lang } = useTranslation('common');
  const {
    description = '',
    hkhsCode = '',
    currency = 'USD',
    naturalAmount,
    qty = 1,
    isIncludesFood = false,
    isIncludesBattery = false,
    countryOfManufacture = '',
    remarks = '',
    onSubmit,
    onRemove,
  } = props;
  const resolver = yupResolver(itemSchema);
  const { formState, handleSubmit, register, control, setValue } = useForm({
    resolver,
    defaultValues: {
      description,
      hkhsCode,
      qty,
      currency,
      naturalAmount,
      isIncludesFood,
      isIncludesBattery,
      countryOfManufacture,
      remarks,
    },
  });

  const { errors, isSubmitting } = formState;
  const togglePopover = usePopoverOpen();

  const [isFetching, setIsFetching] = useState(false);
  const [options, setOptions] = useState<ItemSuggestion[]>([]);

  const fetchSuggestedItems = async (description = '') => {
    try {
      setIsFetching(true);
      let options: ItemSuggestion[] = [];
      if (description) {
        options = await getSuggestedItems(description);
      }
      setOptions(options);
      setIsFetching(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        Logger.error(`[ItemForm] - ${err}`);
        setOptions([]);
        setIsFetching(false);
      }
    }
  };

  const debouncedFetchSuggestedItems = useCallback(_debounce(fetchSuggestedItems, 800), []);

  useEffect(() => {
    analytics.sendItemFormViewed();
  }, []);

  const handleUpdate = (data: TypeOf<typeof itemSchema>) => {
    onSubmit(data);
    togglePopover(false);
    analytics.sendItemUpdated(data.description);
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
      togglePopover(false);
      analytics.sendItemDeleted(description);
    }
  };

  const countries = useMemo(
    () =>
      sortBy(
        COUNTRIES.map((it) => ({
          name: it.name,
          alpha2: it['alpha-2'],
          label: t(`countries:Country.${it['alpha-2']}`),
        })),
        (country) => (country.alpha2 === 'CN' ? 0 : country.alpha2 === 'HK' ? 1 : 2),
      ),
    [],
  );

  return (
    <Stack gap="lg" as="form" className={stackStyle} onSubmit={handleSubmit(handleUpdate)}>
      <FormControl
        helpMessage={
          <Fragment>
            <span className={helpMessageStyle}>{t('Items.itemNameDescription')}</span>
            <Link
              href={t('URL.prohibitedItems')}
              data-tracking-label={t('commonEn:Items.forbiddenItemList')}
            >
              {t('Items.forbiddenItemList')}
            </Link>
          </Fragment>
        }
        errorMessage={errors?.description?.message && t(errors?.description?.message)}
      >
        <Controller
          control={control}
          name="description"
          defaultValue={description}
          render={({ field }) => {
            const { onChange, value } = field;
            const dropdownOptions = options.map((o) => o.description);

            const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
              const description = e.currentTarget.value;
              e.currentTarget.select();
              fetchSuggestedItems(description);
            };

            const onInputChange = (inputValue: string) => {
              // fill hkhsCode from options
              const { hkhsCode } =
                options.find(
                  ({ description }) => description.toLowerCase() === inputValue.toLowerCase(),
                ) || {};
              onChange(inputValue);
              setValue('hkhsCode', hkhsCode ?? '');
              debouncedFetchSuggestedItems(inputValue);
            };

            return (
              <Autocomplete
                sync
                id="description"
                name="description"
                label={t('Items.formLabelItemName')}
                selectedItem={value}
                items={dropdownOptions}
                onSelectedItemChange={onChange}
                onInputValueChange={onInputChange}
                onFocus={onInputFocus}
                disabled={isSubmitting}
                isLoading={isFetching}
                isError={!!errors?.description?.message}
              />
            );
          }}
        />
      </FormControl>
      <FormControl
        errorMessage={
          (errors?.currency?.message && t(errors?.currency?.message)) ||
          (errors?.naturalAmount?.message && t(errors?.naturalAmount?.message))
        }
      >
        <FormLabel htmlFor="naturalAmount">{t('Items.formLabelItemValue')}</FormLabel>
        <Controller
          control={control}
          name="naturalAmount"
          render={({ field }) => {
            const { onChange, ...input } = field;
            return (
              <Input
                as={CurrencyInput}
                id="naturalAmount"
                prependAddon={
                  <select
                    {...register('currency')}
                    id="currency"
                    defaultValue={currency}
                    className={selectStyle}
                    disabled={isSubmitting}
                  >
                    {currencyList.map(({ code }) => (
                      <option value={code} key={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                }
                disabled={isSubmitting}
                isError={!!(errors?.currency?.message || errors?.naturalAmount?.message)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore: the "as" prop for linaria
                onValueChange={onChange}
                {...input}
              />
            );
          }}
        />
      </FormControl>
      <FormControl errorMessage={errors?.qty?.message && t(errors?.qty?.message)}>
        <Controller
          control={control}
          name="qty"
          render={({ field }) => {
            const { onChange, onBlur: _, ...remain } = field;
            return (
              <NumberInput
                id="qty"
                min={1}
                onChange={onChange}
                disabled={isSubmitting}
                isError={!!errors?.qty?.message}
                prependAddon={<FormLabel htmlFor="qty">{t('Items.formLabelQty')}</FormLabel>}
                {...remain}
              />
            );
          }}
        />
      </FormControl>
      <FormControl
        errorMessage={
          errors.countryOfManufacture?.message && t(errors.countryOfManufacture.message)
        }
      >
        <Controller
          control={control}
          name="countryOfManufacture"
          defaultValue={countryOfManufacture}
          render={({ field }) => {
            const { onChange, value } = field;

            const props = makeCountryAutocompleteProps({
              lang,
              id: 'countryOfManufacture',
              label: t('Items.formLabelCountryOfManufacture'),
              selectedCountryAlpha2: value,
              countries,
              isLoading: false,
              isError: !!errors?.countryOfManufacture?.message,
              disabled: isSubmitting,
              onChange,
            });

            return <Autocomplete {...props} />;
          }}
        />
      </FormControl>
      <FormControl errorMessage={errors.remarks?.message && t(errors.remarks.message)}>
        <FormLabel htmlFor="remarks">{t('Items.formLabelRemarks')}</FormLabel>
        <Input
          {...register('remarks')}
          id="remarks"
          disabled={isSubmitting}
          isError={!!errors?.remarks?.message}
        />
      </FormControl>
      <FormControl>
        <Controller
          control={control}
          name="isIncludesFood"
          render={({ field }) => {
            const { onChange, onBlur: _, value, ...remain } = field;
            return (
              <Checkbox
                id="isIncludesFood"
                onChange={onChange}
                disabled={isSubmitting}
                checked={value}
                {...remain}
              >
                {t('Items.isIncludesFood')}
              </Checkbox>
            );
          }}
        />
        <Link className={exportRequirementLinkStyle} href={t('URL.exportRequirementsOfFood')}>
          {t('Items.exportRequirementsOfFood')}
        </Link>
      </FormControl>
      <FormControl>
        <Controller
          control={control}
          name="isIncludesBattery"
          render={({ field }) => {
            const { onChange, onBlur: _, value, ...remain } = field;
            return (
              <Checkbox
                id="isIncludesBattery"
                onChange={onChange}
                disabled={isSubmitting}
                checked={value}
                {...remain}
              >
                {t('Items.isIncludesBattery')}
              </Checkbox>
            );
          }}
        />
        <Link className={exportRequirementLinkStyle} href={t('URL.exportRequirementsOfBattery')}>
          {t('Items.exportRequirementsOfBattery')}
        </Link>
      </FormControl>
      <Text size="sm" className={supportTextStyle}>
        <Trans
          i18nKey={t('Items.forbiddenItemSupport')}
          components={{
            /* eslint-disable jsx-a11y/anchor-has-content */
            /* eslint-disable @typescript-eslint/naming-convention */
            link: (
              <a
                href={t('URL.prohibitedItems')}
                target="_blank"
                rel="noreferrer"
                aria-label="prohibited items support article"
              />
            ),
            /* eslint-enable jsx-a11y/anchor-has-content */
            /* eslint-enable @typescript-eslint/naming-convention */
          }}
        />
      </Text>
      <span className={inlineStyle}>
        <img src={email} alt={t('CustomerSupport.emailIcon')} />
        <a className={mailTextStyle} href={`mailto:${t('CustomerSupport.supportEmail')}`}>
          {t('CustomerSupport.supportEmail')}
        </a>
      </span>
      <Button
        type="submit"
        variant="primary"
        disabled={isFetching}
        loading={isSubmitting}
        data-tracking-label={
          onRemove ? t('commonEn:Items.buttonUpdateItem') : t('commonEn:Items.buttonAddItem')
        }
      >
        {onRemove ? t('Items.buttonUpdateItem') : t('Items.buttonAddItem')}
      </Button>
      {onRemove && (
        <Button
          onClick={handleRemove}
          loading={isSubmitting}
          data-tracking-label={t('commonEn:Items.buttonDeleteItem')}
        >
          {t('Items.buttonDeleteItem')}
        </Button>
      )}
    </Stack>
  );
};

export default ItemForm;
